// workspaces#index — clickable table rows plus the "New workspace" modal.
// The modals are built on <dialog>, so Escape and the focus trap come from the
// browser; this just wires the open/close buttons and click-outside-to-dismiss.
//
// While DISABLE_NEW_PLANS is set, both buttons open the beta wall instead of
// either of the modals below — see beta_modal.ts.
//
// "New workspace" opens one of two: the create form, or — when the
// organization is already at its plan's workspace cap — the limit modal that
// points at /plans. The server decides which by stamping data-at-limit on the
// page, and enforces the cap again on POST regardless.
import { openBetaGate, mountBetaModal } from "../beta_modal"
import { registerPage } from "../first"
import { mountRowLinks } from "../row_links"
import { mountWorkspaceMenus } from "../workspace/workspace_menu"

export const WorkspacesIndexPage = {
  onDomReady(): void {
    mountRowLinks()
    mountWorkspaceMenus()
    // Only does anything while DISABLE_NEW_PLANS is set, and then only to
    // reopen the wall after a POST that tried anyway.
    mountBetaModal()

    const dialog = document.querySelector<HTMLDialogElement>("[data-workspace-modal]")
    const limitDialog = document.querySelector<HTMLDialogElement>("[data-workspace-limit-modal]")
    if (!dialog) return

    const atLimit = document.querySelector<HTMLElement>("[data-workspaces-root]")?.dataset.atLimit === "true"

    document.querySelectorAll<HTMLButtonElement>("[data-workspace-modal-open]").forEach((button) => {
      button.addEventListener("click", () => {
        // Closed for the beta: nothing else on this page applies.
        if (openBetaGate()) return

        if (atLimit && limitDialog) {
          limitDialog.showModal()
        } else {
          open(dialog)
        }
      })
    })

    dismissable(dialog, "[data-workspace-modal-close]")
    if (limitDialog) dismissable(limitDialog, "[data-workspace-limit-modal-close]")

    // Server-side validation failed, so come back with the form open and the
    // user's text still in it.
    if ("workspaceModalOpenOnLoad" in dialog.dataset) open(dialog)
    // The POST met the cap the page was already hiding the form behind.
    if (limitDialog && "workspaceLimitModalOpenOnLoad" in limitDialog.dataset) limitDialog.showModal()
  },
}

registerPage("WorkspacesIndexPage", WorkspacesIndexPage)

function open(dialog: HTMLDialogElement): void {
  dialog.showModal()
  dialog.querySelector<HTMLInputElement>("[data-workspace-modal-autofocus]")?.focus()
}

// Close buttons, plus click-outside: the dialog element fills the viewport, so
// a click that lands on it rather than on the panel inside is the backdrop.
function dismissable(dialog: HTMLDialogElement, closeSelector: string): void {
  dialog.querySelectorAll<HTMLButtonElement>(closeSelector).forEach((button) => {
    button.addEventListener("click", () => dialog.close())
  })

  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dialog.close()
  })
}
